import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Radio, Zap, CheckCircle2 } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal, Stagger, StaggerItem, Overline, SectionShell } from "../components/Reveal";
import { CtaSection } from "../components/CtaSection";
import { INDUSTRIES, IMAGES } from "../content";

const TELECOM_EXTRA = {
  heading: "Wireline & Wireless",
  body: "The telecommunications sector is ever-changing. DCS has been providing domain-specific GIS solutions across the telecom landscape — cable and broadband services, network planning and design for FTTP, FTTN & HFC technologies, and geo-data for fixed wireless and mobile wireless (GSM, CDMA, TDMA, UMTS and WiMAX) networks. Our mapping solutions power wireless and cellular operators, equipment suppliers and planning consultants — from preliminary regional planning and coverage analysis to micro-cell design and line-of-sight studies.",
  capabilities: ["Network Planning & Design (FTTP / FTTN / HFC)", "OSP & ISP GIS Data Management", "Application Development", "DEM/DTM, Land Use / Clutter databases", "3D building models for RF planning", "Coverage analysis & micro-cell design"],
};

const UTILITIES_EXTRA = {
  heading: "Electric & Gas",
  body: "DCS provides solutions in the utility sector through user-friendly, quality-based and cost-effective GIS services — asset management, network analysis, GIS-based consumer indexing and day-to-day performance analysis. Our solutions span the entire value chain from records conversion and management, to supporting critical engineering and business applications for electric transmission and distribution, and distribution integrity management for gas networks.",
  capabilities: ["Asset Data Management & Consumer Indexing", "Network Management with traceability", "Spatial analytics — load flow & energy audit", "Door-to-door survey support", "Gas Distribution Integrity Management", "Engineering drafting to municipal regulation"],
};

export default function Industries() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [hash]);

  const sections = [
    { id: "telecom", icon: Radio, img: IMAGES.city_night, industry: INDUSTRIES[0], extra: TELECOM_EXTRA },
    { id: "utilities", icon: Zap, img: IMAGES.city_lights, industry: INDUSTRIES[1], extra: UTILITIES_EXTRA },
  ];

  return (
    <main data-testid="industries-page">
      <PageHero
        overline="INDUSTRIES"
        title="Networks and grids,"
        highlight="mapped end to end."
        desc="Telecom operators and electric & gas utilities run on spatial truth. DCS keeps that truth accurate, current and production-ready."
        image={IMAGES.city_night}
      />

      {sections.map(({ id, icon: Icon, img, industry, extra }, idx) => (
        <SectionShell key={id} id={id} className="scroll-mt-24">
          <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-start ${idx % 2 ? "" : ""}`} data-testid={`industry-section-${id}`}>
            <Reveal className={idx % 2 ? "lg:order-2" : ""}>
              <div className="img-clip-frame relative rounded-2xl overflow-hidden border border-white/10">
                <img src={img} alt={industry.title} className="w-full h-[380px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#05070A] via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-cyan-400/15 border border-cyan-400/25 backdrop-blur flex items-center justify-center">
                    <Icon size={20} className="text-cyan-400" />
                  </div>
                  <div>
                    <p className="font-display text-xl font-bold text-white">{industry.title}</p>
                    <p className="font-mono-tech text-[10px] tracking-[0.2em] text-cyan-300 uppercase">{extra.heading}</p>
                  </div>
                </div>
              </div>
            </Reveal>
            <div className={idx % 2 ? "lg:order-1" : ""}>
              <Reveal><Overline>{industry.subtitle.toUpperCase()}</Overline></Reveal>
              <Reveal delay={0.08}>
                <p className="mt-4 text-slate-300 leading-relaxed">{extra.body}</p>
              </Reveal>
              <Stagger className="mt-7 grid sm:grid-cols-2 gap-3.5">
                {extra.capabilities.map((c) => (
                  <StaggerItem key={c} className="flex items-start gap-2.5 text-sm text-slate-200 bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3">
                    <CheckCircle2 size={15} className="text-cyan-400 shrink-0 mt-0.5" /> {c}
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
          </div>
        </SectionShell>
      ))}
      <CtaSection />
    </main>
  );
}
